/**
 * TanStack Query hooks for the admin feedback queue (STORY-010-005).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  closeFeedback,
  feedbackQueryKeys,
  getFeedback,
  listFeedback,
  reopenFeedback,
  replyFeedback,
  type FeedbackDetail,
  type FeedbackListResponse,
  type FeedbackReplyRequest,
} from "@/features/admin/feedback/api";

const REFETCH_INTERVAL = 30_000;

export function useFeedbackList(
  status: string,
  includeSpam: boolean,
): UseInfiniteQueryResult<InfiniteData<FeedbackListResponse>, Error> {
  return useInfiniteQuery<
    FeedbackListResponse,
    Error,
    InfiniteData<FeedbackListResponse>,
    ReturnType<typeof feedbackQueryKeys.list>,
    string | null
  >({
    queryKey: feedbackQueryKeys.list({ status, includeSpam }),
    queryFn: ({ pageParam }) =>
      listFeedback({ status, include_spam: includeSpam, cursor: pageParam, limit: 50 }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: 10_000,
  });
}

export function useFeedbackDetail(
  id: string | null,
): UseQueryResult<FeedbackDetail, Error> {
  return useQuery<FeedbackDetail, Error>({
    queryKey: feedbackQueryKeys.detail(id ?? ""),
    queryFn: () => getFeedback(id!),
    enabled: id !== null,
    staleTime: 0,
  });
}

function useInvalidate(): (id: string) => Promise<void> {
  const qc = useQueryClient();
  return async (id: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["admin", "feedback", "list"] }),
      qc.invalidateQueries({ queryKey: feedbackQueryKeys.detail(id) }),
    ]);
  };
}

export function useReplyFeedback(): UseMutationResult<
  FeedbackDetail,
  Error,
  { id: string; body: FeedbackReplyRequest }
> {
  const invalidate = useInvalidate();
  return useMutation<FeedbackDetail, Error, { id: string; body: FeedbackReplyRequest }>({
    mutationFn: ({ id, body }) => replyFeedback(id, body),
    onSuccess: (_d, { id }) => invalidate(id),
  });
}

export function useCloseFeedback(): UseMutationResult<FeedbackDetail, Error, string> {
  const invalidate = useInvalidate();
  return useMutation<FeedbackDetail, Error, string>({
    mutationFn: (id) => closeFeedback(id),
    onSuccess: (_d, id) => invalidate(id),
  });
}

export function useReopenFeedback(): UseMutationResult<FeedbackDetail, Error, string> {
  const invalidate = useInvalidate();
  return useMutation<FeedbackDetail, Error, string>({
    mutationFn: (id) => reopenFeedback(id),
    onSuccess: (_d, id) => invalidate(id),
  });
}
